<template>
  <div class="file-upload-wrapper">
    <div class="upload-container">
      <!-- 页面标题区 -->
      <div class="header-section">
        <h2 class="title">AI 文件解析</h2>
        <p class="subtitle">填写工程型号并上传目标文件夹即可开始</p>
      </div>

      <!-- 工程型号填写区 -->
      <div class="form-section">
        <div class="label-text">
          <span class="required-star">*</span> 工程型号
        </div>
        <el-input
            ref="projectInput"
            v-model="projectId"
            placeholder="请输入工程型号..."
            class="project-input"
            clearable
        >
          <template #prefix>
            <el-icon><Document /></el-icon>
          </template>
        </el-input>
      </div>

      <!-- 文件/文件夹拖拽与选择区 -->
      <div class="upload-section">
        <div class="label-text">
          <span class="required-star">*</span> 目标文件夹
        </div>
        <div
            class="drag-upload-area"
            :class="{ 'has-file': filePath }"
            @dragover.prevent
            @drop.prevent="handleDrop"
            @click="triggerFileSelect"
        >
          <!-- 未选择文件时的占位 -->
          <div v-if="!filePath" class="upload-placeholder">
            <el-icon class="upload-icon"><FolderOpened /></el-icon>
            <div class="upload-text">点击选择 或 拖拽文件夹至此处</div>
            <div class="upload-tip">支持上传大小在 50M 以内的文件夹</div>
          </div>

          <!-- 选中文件后展示路径 -->
          <div v-else class="upload-selected">
            <el-icon class="success-icon"><FolderChecked /></el-icon>
            <div class="selected-title">已选文件夹</div>
            <div class="selected-path" :title="filePath">{{ filePath }}</div>
            <div class="reselect-tip">点击或拖拽可重新选择</div>
          </div>
        </div>
      </div>

      <!-- 操作按钮区 -->
      <div class="action-section">
        <!-- 保持原有的简洁默认按钮色系 -->
        <el-button class="start-parse-btn" @click="startParsing">
          开始 AI 解析
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { Document, FolderOpened, FolderChecked } from "@element-plus/icons-vue";
import { syncProjectFile } from "@/components/file_process_js/main_process";

export default {
  name: "FileUpload",
  components: {
    Document,
    FolderOpened,
    FolderChecked
  },
  data() {
    return {
      projectId: "", // 存储工程型号
      filePath: "",  // 存储选择的文件或文件夹路径
    };
  },
  methods: {
    // 点击时触发文件或文件夹选择对话框
    async triggerFileSelect() {
      const lastImportPath = localStorage.getItem('LAST_IMPORT_PATH');
      const filePathList = await window.electronAPI.selectFile({
        mode: 'directory',
        defaultPath: lastImportPath
      });

      if (filePathList && filePathList.length > 0) {
        const selectedPath = filePathList[0];
        this.filePath = selectedPath;
        localStorage.setItem('LAST_IMPORT_PATH', selectedPath);
        console.log("选中的文件或文件夹路径:", selectedPath);
      }
    },

    // 处理文件或文件夹拖拽操作
    handleDrop(event) {
      const files = event.dataTransfer.files;
      if (files.length) {
        const file = files[0];
        this.filePath = file.path || file.file_key;
        console.log("拖拽的文件路径:", this.filePath);
      }
    },

    // 开始AI解析的逻辑
    startParsing() {
      // 校验逻辑
      if (!this.projectId.trim()) {
        ElMessage.warning("请先填写工程型号");
        // 如果未填写，聚焦到输入框
        this.$nextTick(() => {
          this.$refs.projectInput.focus();
        });
        return;
      }
      if (!this.filePath) {
        ElMessage.warning("请选择或拖入需要解析的文件夹");
        return;
      }

      console.log("开始AI解析，工程型号:", this.projectId, "文件路径:", this.filePath);

      // 存入工程型号到 Store
      this.$store.commit("setProjectId", this.projectId.trim());

      this.goToResultsPage();
    },

    async goToResultsPage() {
      // 使用 Vuex 保存文件路径，并跳转到结果页
      this.$store.commit("setCommonObjList", []); // 防止缓存，清空文件列表
      this.$store.commit("setGerberObjList", []); // 防止缓存，清空文件列表
      this.$store.commit("setFilePath", this.filePath);
      this.$store.commit("clearPollingList");

      await syncProjectFile(this.filePath, "init");
      const commonObjList = this.$store.state.commonObjList;
      console.log("goToResultsPage commonObjList", commonObjList);
      this.$router.push({ name: "results" });
    },
  },
};
</script>

<style scoped>
/* 外层容器，取消占满全屏导致的滚动，使用适度的顶部间距 */
.file-upload-wrapper {
  display: flex;
  justify-content: center;
  padding-top: 50px;
  box-sizing: border-box;
}

/* 核心内容区，去除了厚重的阴影，保持扁平简洁 */
.upload-container {
  width: 100%;
  max-width: 600px;
  background-color: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 30px 40px;
}

/* 标题区 */
.header-section {
  text-align: center;
  margin-bottom: 25px;
}

.title {
  font-size: 20px;
  color: #303133;
  margin-bottom: 6px;
  font-weight: 500;
}

.subtitle {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

/* 标签通用样式 */
.label-text {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.required-star {
  color: #f56c6c;
  margin-right: 2px;
}

/* 工程型号输入区 */
.form-section {
  margin-bottom: 20px;
}

.project-input {
  width: 100%;
}

/* 拖拽上传区 */
.upload-section {
  margin-bottom: 25px;
}

/* 回归简洁的虚线框 */
.drag-upload-area {
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background-color: #fafafa;
  height: 160px; /* 压缩了高度，防松动 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 15px;
  text-align: center;
}

.drag-upload-area:hover {
  border-color: #c0c4cc;
  background-color: #f4f4f5;
}

/* 选中文件时的样式，使用克制的浅灰色系代替耀眼的绿色 */
.drag-upload-area.has-file {
  border-color: #b1b3b8;
  background-color: #f4f4f5;
}

.upload-icon, .success-icon {
  font-size: 36px;
  color: #909399;
  margin-bottom: 10px;
}

.upload-text, .selected-title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 6px;
}

.upload-tip, .reselect-tip {
  font-size: 12px;
  color: #a8abb2;
}

.selected-path {
  font-size: 12px;
  color: #606266;
  background-color: #ffffff;
  border: 1px solid #e4e7ed;
  padding: 4px 10px;
  border-radius: 3px;
  width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; /* 单行超出显示省略号，更简洁紧凑 */
  margin-bottom: 10px;
}

/* 操作按钮区 */
.action-section {
  text-align: center;
  margin-top: 10px;
}

/* 保留原本色系，仅调整宽高适应新版式 */
.start-parse-btn {
  width: 260px;
  height: 40px;
  font-size: 14px;
  letter-spacing: 1px;
}
</style>